import { useEffect, useRef, useState } from "react";
import CountUp from "./CountUp";
import "./DashboardPreview.css";

// Hidden until validated figures are available.
const SHOW_METRICS = false;

const SCOPES = [
  { label: "Scope 1", value: 48, color: "#5944c6" },
  { label: "Scope 2", value: 24, color: "#1781e3" },
  { label: "Scope 3", value: 48, color: "#0eaba9" },
];
const YEARS = [2022, 2023, 2024, 2025, 2026];
const EMISSIONS = [180, 168, 149, 137, 120];
const POINTS = EMISSIONS.map((value, index) => `${55 + index * 112},${230 - value}`).join(" ");

export default function DashboardPreview({ onRegisterClick }) {
  const previewRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!window.IntersectionObserver) {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.2 });
    observer.observe(previewRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="dashboard-preview" aria-label="Einblick in GreenOne">
      {/* Illustrative marketing figures; replace with validated figures before publication. */}
      {SHOW_METRICS && (
        <div className="dashboard-preview-metrics">
          <div><strong><CountUp prefix="Ø " to={18} suffix=" %" /></strong><span>tiefere Energiekosten</span></div>
          <div><strong><CountUp prefix="−" to={8} suffix=" Stunden" /></strong><span>Erfassungsaufwand pro Monat</span></div>
          <div><strong><CountUp to={120} suffix=" Betriebe" /></strong><span>nutzen GreenOne</span></div>
        </div>
      )}

      <div ref={previewRef} className={`dashboard-preview-surface${isVisible ? " dashboard-preview-visible" : ""}`}>
        <header className="dashboard-preview-heading">
          <div><p className="dashboard-preview-eyebrow">GREENONE / EMISSIONSÜBERSICHT</p><h3>Fortschritt wird sichtbar.</h3></div>
        </header>
        <div className="dashboard-preview-charts">
          <div className="dashboard-preview-balance">
            <h4>Ihre CO₂-Bilanz</h4>
            <p className="dashboard-preview-note">Verteilung nach Scope · 2026</p>
            <div className="dashboard-preview-donut">
              <svg viewBox="0 0 240 240" role="img" aria-label="120 Tonnen CO₂e: Scope 1 48 Tonnen, Scope 2 24 Tonnen, Scope 3 48 Tonnen">
                <circle cx="120" cy="120" r="88" fill="none" stroke="#edf2f2" strokeWidth="25" />
                <g className="dashboard-preview-ring" transform="rotate(-90 120 120)">
                  {SCOPES.map((scope, index) => (
                    <circle key={scope.label} cx="120" cy="120" r="88" fill="none" stroke={scope.color} strokeWidth="25" pathLength="100"
                      strokeDasharray={`${scope.value / 120 * 100 - 1.5} 100`}
                      strokeDashoffset={-SCOPES.slice(0, index).reduce((sum, item) => sum + item.value / 120 * 100, 0)} />
                  ))}
                </g>
              </svg>
              <div className="dashboard-preview-total"><strong>120</strong><span>t CO₂e / Jahr</span></div>
            </div>
            <ul className="dashboard-preview-legend">
              {SCOPES.map(scope => <li key={scope.label}><i style={{ background: scope.color }} />{scope.label}<strong>{scope.value} t</strong></li>)}
            </ul>
          </div>
          <div className="dashboard-preview-trend">
            <div className="dashboard-preview-trend-heading"><div><h4>Weniger Emissionen. Mehr Perspektive.</h4><p className="dashboard-preview-note">Ihr Verlauf in t CO₂e</p></div><strong>−33 %<small>seit 2022</small></strong></div>
            <svg viewBox="0 0 550 245" role="img" aria-label="Beispielverlauf: 2022 180, 2023 168, 2024 149, 2025 137, 2026 120 Tonnen CO₂e">
              {[60, 120, 180].map(value => <g key={value}><line x1="45" x2="515" y1={230 - value} y2={230 - value} stroke="#dde8e7" strokeDasharray="3 5" /><text x="30" y={235 - value} textAnchor="end">{value}</text></g>)}
              <g className="dashboard-preview-curve">
                <polygon points={`55,205 ${POINTS} 503,205`} fill="#e2f2ef" />
                <polyline points={POINTS} fill="none" stroke="#026f6d" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
                {EMISSIONS.map((value, index) => <g key={YEARS[index]}><circle cx={55 + index * 112} cy={230 - value} r="5" fill="white" stroke="#026f6d" strokeWidth="3" /><text x={55 + index * 112} y={212 - value} textAnchor="middle">{value}</text></g>)}
              </g>
              {YEARS.map((year, index) => <text key={year} x={55 + index * 112} y="233" textAnchor="middle">{year}</text>)}
            </svg>
            <p className="dashboard-preview-caption">Erkennen, wo Sie stehen. Verfolgen, was sich verändert.</p>
          </div>
        </div>
      </div>
      <div className="dashboard-preview-cta">
        <h3>Sehen Sie, wo Ihr Betrieb spart.</h3>
        <p>Starten Sie kostenlos. Ohne Vorwissen.</p>
        <button type="button" className="hero-btn hero-btn-primary" onClick={onRegisterClick}>
          Kostenlos registrieren
        </button>
      </div>
    </section>
  );
}
